/* eslint-disable max-depth */
/* eslint-disable indent */
import GridPageAbstract from '../../GridPageAbstract';
import { PersonalcardBulkIcon } from '../../../icons-lib/bulkIcons';
import {
    DocumentDownloadLinearIcon,
    AddCircleWhiteLinearIcon
} from '../../../icons-lib';
import i18n from '../../../i18';
import { GridDataAdapter } from '../../../adapters/GridDataAdapter';
import BatchUploadDialog from '../../dialogs/BatchUploadDialog';
import TopUpDialog from '../../dialogs/TopUpDialog';
import MenuItem from '@mui/material/MenuItem';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MultipleTopUpDialog from '../../dialogs/MultipleTopUpDialog';

export default class SmeCompanyEmployeesPage extends GridPageAbstract {
    constructor(props) {
        super(props);

        this.Name = 'smeEmployees';
        this.controllerName = 'users'; // Set controller name for permissions
        this.permissionName = 'users'; // Set permission name for access control
        this.hideAddAction = false; // Ensure Add button is visible
        this.MenuDisplayType = 'menu'; // Ensure menu is visible
        this.hideSearch = false; // Ensure search is visible
        // Use generic users controller; backend distinguishes SME roles through filters
        this.Server = this.props.context.getState().server.users;

        this.headerStartIcon = PersonalcardBulkIcon;

        this.exportButton = true;

        this.searchFields = ['firstName', 'email', 'smeCompany.name'];

        // Initialize baseFilterRequest with role IDs instead of system role strings
        this.initializeBaseFilterRequest();

        this.Head = [
            {
                id: 'firstName',
                label: 'employeeName',
                alignRight: false,
                cell: 'Text',
                required: true
            },
            {
                id: 'email',
                label: 'email',
                alignRight: false,
                cell: 'Text',
                required: true
            },
            {
                id: 'walletNumber',
                label: 'walletId',
                alignRight: false,
                cell: 'Text',
                disableEditor: true,
                object: 'wallet',
                display: 'walletNumber'
            },
            {
                id: 'roleId',
                label: 'role',
                cell: 'SelectLabel',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                disableGrid: true
            },
            {
                id: 'isActive',
                label: 'status',
                alignRight: false,
                cell: 'Boolean',
                filterCell: 'Boolean',
                trueLabel: 'active',
                falseLabel: 'inactive',
                type: 'boolean'
            },
            {
                id: 'balance',
                label: 'balance',
                alignRight: false,
                cell: 'Amount',
                type: 'amount',
                filterCell: 'Range',
                object: 'wallet',
                display: 'currentBalance'
            },
            {
                id: 'smeCompanyId',
                sortField: 'smeCompany.name',
                label: 'campaignName',
                alignRight: false,
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: {
                    primary: 'name',
                    secondary: 'email'
                },
                required: true
            }
        ];
    }

    // Override componentDidMount to ensure proper initialization
    async componentDidMount() {
        // Initialize base filter request first
        await this.initializeBaseFilterRequest();

        // Call parent componentDidMount
        super.componentDidMount();
    }

    /* -----------------------
     *  Custom Functions
     * ---------------------- */

    // Initialize base filter request with role IDs instead of system role strings
    async initializeBaseFilterRequest() {
        try {
            // Get roles from context or fetch them
            let roles = this.props.context.getState().controllersData.roles;

            if (!roles || roles.length === 0) {
                // Fetch roles if not available in context
                const result = await this.props.context
                    .getState()
                    .server.roles.getAll();
                roles = result.data || [];
            }

            // Find SME role IDs
            const smeRoleIds = roles
                .filter((role) =>
                    ['sme_admin', 'sme_employee'].includes(role.systemRole)
                )
                .map((role) => role.id);

            // Set base filter request using role IDs
            this.baseFilterRequest = [
                {
                    field: 'roleId',
                    op: '@',
                    value: smeRoleIds
                }
            ];

            // Trigger data retrieval if the page is already mounted
            if (this.state && this.state.isLoading !== undefined) {
                this.retrieveData();
            }
        } catch (error) {
            console.error('Failed to initialize base filter request:', error);
            // Fallback to system role strings if role IDs can't be fetched
            this.baseFilterRequest = [
                {
                    field: 'role.systemRole',
                    op: '@',
                    value: ['sme_admin', 'sme_employee']
                }
            ];
        }
    }

    // Handle batch upload completion - called by BatchUploadDialog after successful import
    handleBatchUpload = async (employeesData, smeCompanyId) => {
        try {
            // The BatchUploadDialog already handled the import, just refresh the grid
            this.retrieveData();
        } catch (error) {
            console.error('Batch upload error:', error);
            this.setState({
                snackbarOpen: true,
                alertText: 'Import failed: ' + error.message,
                alertSeverity: 'error'
            });
        }
    };

    // Provide extra header action (next to "Add SME Employee") for batch upload
    getHeaderActions = () => [
        {
            title: i18n.t('buttons.batchUploadSmeEmployees'),
            onClick: () => this.openBatchUploadDialog(),
            icon: <AddCircleWhiteLinearIcon />
        }
    ];

    // No additional toolbar actions for now
    getToolbarActions = () => {
        const { selected } = this.state;
        const actions = [];

        if (selected && selected.length > 0) {
            actions.push({
                title: 'Top Up',
                onClick: () => this.handleMultipleTopUpOpen(selected)
            });
        }

        return actions;
    };

    // Custom menu items for each row - only edit, delete, and top-up
    renderCustomMenuItems() {
        const items = [];

        // Add top-up option for users with update permission
        if (this.allowedPermission('update')) {
            items.push(
                <MenuItem
                    onClick={(event) => {
                        const row = this.List.find(
                            (r) => r.id === this.state.menuClickItemId
                        );
                        if (row) this.handleTopUp(row);
                        this.handleCloseMenu();
                    }}
                    key="topUp"
                >
                    <AccountBalanceWalletIcon sx={{ mr: 2 }} />
                    {i18n.t('buttons.topUpWallet')}
                </MenuItem>
            );
        }

        return items.length > 0 ? <>{items}</> : null;
    }

    // Custom dialog for batch upload
    openBatchUploadDialog() {
        this.setState({
            batchUploadDialogOpen: true
        });
    }

    // Handle top-up wallet
    handleTopUp = (row) => {
        this.setState({
            topUpDialogOpen: true,
            selectedEmployee: row
        });
    };

    // Handle top-up submission
    handleTopUpSubmit = async (topUpData) => {
        try {
            // Call the server to perform the top-up
            const payload = {
                fromWalletId: topUpData.fromWallet?.wallet?.id,
                transfers: [
                    {
                        toWalletId: topUpData.toWalletId,
                        amount: parseFloat(topUpData.amount)
                    }
                ]
            };
            await this.props.context
                .getState()
                .server.smeCompanies.topUp(payload);

            // Refresh the grid data
            this.retrieveData();
        } catch (error) {
            console.error('Top-up error:', error);
            this.setState({
                snackbarOpen: true,
                alertText: 'Top-up failed: ' + error.message
            });
            throw error; // Re-throw to let the dialog handle the error
        }
    };

    handleMultipleTopUpOpen = (selectedIds) => {
        const { List } = this;
        console.log('List', List);

        if (!List) {
            console.error('Employee list not available.');
            return;
        }
        const selectedEmployees = List.filter((employee) => {
            console.log('employee', employee);
            return selectedIds.includes(employee.id);
        });
        this.setState({
            multipleTopUpDialogOpen: true,
            selectedEmployees
        });
    };

    handleMultipleTopUpClose = () => {
        this.setState({
            multipleTopUpDialogOpen: false,
            selectedEmployees: undefined
        });
    };

    handleMultipleTopUpSubmit = async (data) => {
        const transfers = data.employees.map((e) => ({
            toWalletId: e.wallet?.id,
            amount: parseFloat(data.eachAmount)
        }));

        const payload = {
            fromWalletId: data.fromWallet?.wallet?.id,
            transfers
        };

        try {
            await this.props.context
                .getState()
                .server.smeCompanies.topUp(payload);
            this.retrieveData();
        } catch (e) {
            this.setState({
                snackbarOpen: true,
                alertText: e.message || 'Top-up failed'
            });
        }

        this.handleMultipleTopUpClose();
    };

    // Form fields for create dialog
    getCreateFields() {
        return [
            {
                id: 'firstName',
                label: 'name',
                cell: 'Text',
                required: true
            },
            {
                id: 'email',
                label: 'email',
                cell: 'Text',
                required: true
            },
            {
                id: 'topUpAmount',
                label: 'topUpAmount',
                cell: 'Amount',
                type: 'amount',
                required: false,
                context: this.props.context
            },
            {
                id: 'roleId',
                label: 'role',
                cell: 'Select',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                required: true,
                itemsFilter: ['sme_employee'],
                defaultValue: 'sme_employee'
            },
            {
                id: 'smeCompanyId',
                label: 'campaignName',
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: {
                    primary: 'name',
                    secondary: 'email'
                },
                required: true
            },
            // Hidden fields with default values
            {
                id: 'isActive',
                label: 'isActive',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'twoFactorEnabled',
                label: 'twoFactor',
                cell: 'Boolean',
                defaultValue: false,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'emailVerified',
                label: 'emailVerified',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'language',
                label: 'language',
                cell: 'Items',
                items: ['en'],
                defaultValue: 'en',
                required: true,
                hideInEditor: true
            }
        ];
    }

    // Form fields for edit dialog
    getEditFields() {
        return [
            {
                id: 'firstName',
                label: 'name',
                cell: 'Text',
                required: true
            },
            {
                id: 'email',
                label: 'email',
                cell: 'Text',
                required: true
            },
            {
                id: 'topUpAmount',
                label: 'topUpAmount',
                cell: 'Amount',
                type: 'amount',
                required: false,
                context: this.props.context
            },
            {
                id: 'roleId',
                label: 'role',
                cell: 'Select',
                from: 'roles',
                display: 'name',
                enumType: true,
                object: 'role',
                required: true,
                itemsFilter: ['sme_admin', 'sme_employee']
            },
            {
                id: 'smeCompanyId',
                label: 'campaignName',
                cell: 'Associated',
                from: 'smeCompanies',
                searchType: 'server',
                searchFields: ['name', 'email'],
                searchController: 'sme-companies',
                display: 'name',
                object: 'smeCompany',
                associatedSettings: {
                    primary: 'name',
                    secondary: 'email'
                },
                required: true
            },
            // Hidden fields with default values
            {
                id: 'isActive',
                label: 'isActive',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'twoFactorEnabled',
                label: 'twoFactor',
                cell: 'Boolean',
                defaultValue: false,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'emailVerified',
                label: 'emailVerified',
                cell: 'Boolean',
                defaultValue: true,
                type: 'boolean',
                hideInEditor: true
            },
            {
                id: 'language',
                label: 'language',
                cell: 'Items',
                items: ['en'],
                defaultValue: 'en',
                required: true,
                hideInEditor: true
            }
        ];
    }

    // Override dialogOpen to use custom fields
    dialogOpen(data) {
        const isEditMode = !!(data && data.id);
        const fields = isEditMode
            ? this.getEditFields()
            : this.getCreateFields();

        const dialogData = GridDataAdapter.transform(data, fields);

        // Preselect SME Employee role for new entries
        if (!isEditMode) {
            try {
                const roles =
                    this.props.context.getState().controllersData.roles || [];
                const smeRole = roles.find(
                    (r) => r.systemRole === 'sme_employee'
                );
                if (smeRole) {
                    dialogData.roleId = smeRole.id;
                }
            } catch (e) {
                console.error('Failed to preselect role', e);
            }
        }

        this.setState({
            dialogOpen: true,
            dialogData,
            isEditMode: isEditMode || false,
            open: false
        });
    }

    // Override renderDialog to use custom fields
    renderDialog() {
        // Temporarily override Head with custom fields
        const originalHead = this.Head;
        this.Head = this.state?.isEditMode
            ? this.getEditFields()
            : this.getCreateFields();

        const result = super.renderDialog();

        // Restore original Head
        this.Head = originalHead;

        return result;
    }

    // Custom render method for additional components
    renderCustom() {
        return (
            <>
                <BatchUploadDialog
                    open={this.state.batchUploadDialogOpen || false}
                    onClose={() =>
                        this.setState({ batchUploadDialogOpen: false })
                    }
                    onUpload={(employeesData, companyId) =>
                        this.handleBatchUpload(employeesData, companyId)
                    }
                    context={this.props.context}
                    userInfo={this.userInfo}
                />
                <TopUpDialog
                    open={this.state.topUpDialogOpen || false}
                    onClose={() => this.setState({ topUpDialogOpen: false })}
                    onTopUp={this.handleTopUpSubmit}
                    context={this.props.context}
                    employeeData={this.state.selectedEmployee}
                    userInfo={this.userInfo}
                />
                <MultipleTopUpDialog
                    open={this.state.multipleTopUpDialogOpen || false}
                    onClose={this.handleMultipleTopUpClose}
                    onTopUp={this.handleMultipleTopUpSubmit}
                    employees={this.state.selectedEmployees || []}
                    context={this.props.context}
                    userInfo={this.userInfo}
                />
            </>
        );
    }

    // Override handleDialogOnSave to handle the save flow properly
    async handleDialogOnSave(data) {
        // If this is an edit operation (user has an ID), include currentRoleId
        if (data.id) {
            // Get the current user data to find their current role
            const currentUserData = this.List.find(
                (user) => user.id === data.id
            );
            if (currentUserData && currentUserData.roleId) {
                data.currentRoleId = currentUserData.roleId;
            }
        }

        // Validate topUpAmount against wallet balance if provided
        if (data.topUpAmount && data.topUpAmount > 0) {
            try {
                // Get the SME company to check its wallet balance
                const smeCompanyId = data.smeCompanyId;
                if (smeCompanyId) {
                    const smeCompany = await this.props.context
                        .getState()
                        .server.smeCompanies.getById(smeCompanyId);

                    if (smeCompany && smeCompany.wallet) {
                        const walletBalance = parseFloat(
                            smeCompany.wallet.currentBalance || 0
                        );
                        const topUpAmount = parseFloat(data.topUpAmount);

                        if (topUpAmount > walletBalance) {
                            this.setState({
                                alertText: i18n.t('errors.insufficientBalance'),
                                alertSeverity: 'error',
                                snackbarOpen: true
                            });
                            return; // Don't proceed with the save
                        }
                    }
                }
            } catch (error) {
                console.error('Error validating topUpAmount:', error);
                this.setState({
                    alertText:
                        'Error validating top-up amount: ' + error.message,
                    alertSeverity: 'error',
                    snackbarOpen: true
                });
                return; // Don't proceed with the save
            }
        }

        // Close dialog and refresh data (this is what the parent does)
        this.setState({ dialogOpen: false, dialogData: null });
        await this.retrieveData();
    }
}
